import { useState } from 'react';
import {
  Card,
  Group,
  Avatar,
  Text,
  ActionIcon,
  Stack,
  Badge,
  Collapse,
  UnstyledButton,
  Divider
} from '@mantine/core';
import {
  IconChevronDown,
  IconChevronUp,
  IconUser,
  IconStar,
  IconBook
} from '@tabler/icons-react';
import { capitalizeName } from '@/utils/name';

interface Child {
  id: string;
  name: string;
  avatar?: string;
  grade?: number;
  readingStreak?: number;
  lastActive?: string;
}

interface MobileChildSelectorProps {
  children: Child[];
  selectedChildId: string | null;
  onChildSelect: (childId: string) => void;
}

export function MobileChildSelector({
  children,
  selectedChildId,
  onChildSelect
}: MobileChildSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);

  const selectedChild = children.find(c => c.id === selectedChildId);

  if (children.length === 0) {
    return (
      <Card p="md" withBorder>
        <Text size="sm" c="dimmed" align="center">
          No children found. Add a child to get started.
        </Text>
      </Card>
    );
  }

  if (children.length === 1) {
    // If only one child, show a simple card without selector
    const child = children[0];
    return (
      <Card p="sm" withBorder>
        <Group>
          <Avatar size={40} radius="xl" color="blue">
            {child.avatar ? (
              <img src={child.avatar} alt={capitalizeName(child.name)} />
            ) : (
              <IconUser size={20} />
            )}
          </Avatar>
          <div style={{ flex: 1 }}>
            <Text size="sm" fw={500}>
              {capitalizeName(child.name)}
            </Text>
            <Group gap="xs">
              {child.grade && (
                <Badge size="xs" variant="light">
                  Grade {child.grade}
                </Badge>
              )}
              {child.readingStreak && child.readingStreak > 0 && (
                <Badge size="xs" variant="light" color="orange" leftSection={<IconStar size={10} />}>
                  {child.readingStreak} day streak
                </Badge>
              )}
            </Group>
          </div>
        </Group>
      </Card>
    );
  }

  const handleChildSelect = (childId: string) => {
    onChildSelect(childId);
    setIsOpen(false);
  };

  return (
    <div>
      <Card p="sm" withBorder style={{ cursor: 'pointer' }}>
        <UnstyledButton
          onClick={() => setIsOpen(!isOpen)}
          style={{ width: '100%' }}
        >
          <Group justify="space-between">
            <Group>
              <Avatar size={40} radius="xl" color="blue">
                {selectedChild?.avatar ? (
                  <img src={selectedChild.avatar} alt={capitalizeName(selectedChild.name)} />
                ) : (
                  <IconUser size={20} />
                )}
              </Avatar>
              <div>
                <Text size="sm" fw={500}>
                  {capitalizeName(selectedChild?.name) || 'Select a child'}
                </Text>
                {selectedChild && (
                  <Group gap="xs">
                    {selectedChild.grade && (
                      <Badge size="xs" variant="light">
                        Grade {selectedChild.grade}
                      </Badge>
                    )}
                    {selectedChild.readingStreak && selectedChild.readingStreak > 0 && (
                      <Badge size="xs" variant="light" color="orange">
                        <Group gap={4}>
                          <IconStar size={10} />
                          {selectedChild.readingStreak}
                        </Group>
                      </Badge>
                    )}
                  </Group>
                )}
              </div>
            </Group>
            <ActionIcon variant="subtle" size="sm">
              {isOpen ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
            </ActionIcon>
          </Group>
        </UnstyledButton>
      </Card>

      <Collapse in={isOpen}>
        <Card p={0} withBorder mt="xs" style={{ borderTop: 0 }}>
          <Stack gap={0}>
            {children
              .filter(child => child.id !== selectedChildId)
              .map((child, index) => (
                <div key={child.id}>
                  {index > 0 && <Divider />}
                  <UnstyledButton
                    onClick={() => handleChildSelect(child.id)}
                    style={{ width: '100%', padding: '12px' }}
                  >
                    <Group>
                      <Avatar size={36} radius="xl" color="blue">
                        {child.avatar ? (
                          <img src={child.avatar} alt={capitalizeName(child.name)} />
                        ) : (
                          <IconUser size={18} />
                        )}
                      </Avatar>
                      <div style={{ flex: 1 }}>
                        <Text size="sm" fw={500}>
                          {capitalizeName(child.name)}
                        </Text>
                        <Group gap="xs">
                          {child.grade && (
                            <Text size="xs" c="dimmed">
                              Grade {child.grade}
                            </Text>
                          )}
                          {child.lastActive && (
                            <Text size="xs" c="dimmed">
                              Active {child.lastActive}
                            </Text>
                          )}
                        </Group>
                      </div>
                      {child.readingStreak && child.readingStreak > 0 && (
                        <Badge size="sm" variant="light" color="orange">
                          <Group gap={4}>
                            <IconBook size={12} />
                            {child.readingStreak}
                          </Group>
                        </Badge>
                      )}
                    </Group>
                  </UnstyledButton>
                </div>
              ))}
          </Stack>
        </Card>
      </Collapse>
    </div>
  );
}